import { useCallback, useMemo, useState } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { GraphNodeConfigFormData } from 'dashboard/forms/FormGraphNodeConfiguration/FormGraphNodeConfiguration';
import { PRESET_TYPE_TO_LABEL } from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/NodeFunctionsConfigurationTab';
import SourceFunctionMapping, {
  getFieldSchemas,
} from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/SourceFunctionMapping';
import SourceFunctionMappingFixed from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/SourceFunctionMappingFixed';
import { AccountType } from 'dashboard/models/account-type';
import {
  NodeFunction,
  NodeFunctionsSource,
  ParamMapping,
  ServerPresetSummary,
} from 'dashboard/models/graph-backend';
import SmartMappingBanner from 'dashboard/views/DashboardImporterView/SmartMappingBanner';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text/Text';
import { flattenFieldSchema } from 'lib/fields';
import { TextEquivalenceModel } from 'lib/TextEquivalenceModel';

export interface NodeFunctionConfigurationProps {
  watch: UseFormWatch<GraphNodeConfigFormData>;
  setValue: UseFormSetValue<GraphNodeConfigFormData>;
  nodeFunction: NodeFunction;
  accountTypes: AccountType[];
  presetSummary: ServerPresetSummary;
  presetSummaries: ServerPresetSummary[];
  disabled: boolean;
  name: string;
  autoMapperTimeout: NodeJS.Timeout | undefined;
  setAutoMapperTimeout: (timeout: NodeJS.Timeout | undefined) => void;
}

const NodeFunctionConfiguration = ({
  watch,
  setValue,
  nodeFunction,
  presetSummary,
  presetSummaries,
  accountTypes,
  disabled,
  name,
  autoMapperTimeout,
  setAutoMapperTimeout,
}: NodeFunctionConfigurationProps) => {
  const sources = watch('nodeFunctionsConfig.sources');

  const [isError, setIsError] = useState(false);

  const hasMultipleFields = presetSummary['param-schema'].length > 1;
  // If the node function has multiple fields, the auto mapping is based on the field name. If the node function has only one field, the auto mapping is based on the node function name.
  const nodeFunctionFields: string[] = useMemo(
    () => (hasMultipleFields ? presetSummary['param-schema'].map((fs) => fs.key) : [name]),
    [hasMultipleFields, name, presetSummary],
  );

  const generateMappingsAndTypes = useCallback(
    (source: NodeFunctionsSource, accountType: AccountType) => {
      // Get field schema for the source.
      const fieldSchemas = getFieldSchemas(source, accountType);

      // flattenedFieldSchema flattens the fieldSchemas since thats the format TextEquivalenceModel expects.
      // shouldIncludeArrays=false because arrays are their own separate source.
      const flattenedFieldSchema = flattenFieldSchema(fieldSchemas, false);
      const destinationTypeMapping: { [key: string]: string } = flattenedFieldSchema.reduce(
        (acc, fs) => {
          return { ...acc, [fs.value]: fs.type };
        },
        {},
      );

      const personaFields: string[] = flattenedFieldSchema
        .filter((fs) => fs.active)
        .map((fs) => fs.value);

      if (!nodeFunctionFields.length || !personaFields.length) {
        return { autoGeneratedMappings: {}, destinationTypeMapping };
      }

      const autoGeneratedMappings = TextEquivalenceModel.autoMapFields(
        nodeFunctionFields,
        personaFields,
      );

      return { autoGeneratedMappings, destinationTypeMapping };
    },
    [nodeFunctionFields],
  );

  // Filter out mappings with mismatching types. Ideally, we'd probably want TextEquivalenceModel to account for types of fields while mapping and only consider mappings with matching types.
  // However, this isn't supported currently so this should still be sufficient for now.
  const hasMatchingTypes = useCallback(
    (
      newPath: string,
      paramMapping: ParamMapping,
      destinationTypeMapping: { [key: string]: string },
    ) => {
      const nodeFunctionField = presetSummary['param-schema'].find(
        (fs) => fs.key === paramMapping.destination,
      );
      const personaFieldType = destinationTypeMapping[newPath];

      return nodeFunctionField?.type === personaFieldType;
    },
    [presetSummary],
  );

  const autoMapFields = useCallback(() => {
    sources.forEach((source, sourceIdx) => {
      const accountType = accountTypes.find(
        (accountType) => source['object-type-token'] === accountType.id,
      );
      // Only auto map fields for fields using SourceFunctionMapping component (editable).
      if (source['object-type'] !== 'Account' || !accountType) {
        return;
      }

      const functionMappingIdx = source['function-mappings'].findIndex(
        (fm) => fm.token === nodeFunction.token,
      );
      const paramMappings: ParamMapping[] = watch(
        `nodeFunctionsConfig.sources.${sourceIdx}.function-mappings.${functionMappingIdx}.param-mappings`,
      );
      if (!paramMappings) return;

      // NOTE (Tony): For now, only try to auto map fields from the source, not chained function outputs.
      // Maybe could address that in a future pr, but not sure how it'd work/how effective it'd be.
      const { autoGeneratedMappings, destinationTypeMapping } = generateMappingsAndTypes(
        source,
        accountType,
      );

      // Set the auto mapped fields into the form.
      paramMappings.forEach((paramMapping, idx) => {
        const mappingKey = hasMultipleFields ? paramMapping.destination : name;
        const generatedPath = autoGeneratedMappings[mappingKey] || '';

        const newPath = hasMatchingTypes(generatedPath, paramMapping, destinationTypeMapping)
          ? generatedPath
          : '';

        if (paramMapping.path !== newPath) {
          setValue(
            `nodeFunctionsConfig.sources.${sourceIdx}.function-mappings.${functionMappingIdx}.param-mappings.${idx}`,
            { ...paramMapping, path: newPath ? 'source.' + newPath : '' },
            {
              shouldDirty: true,
            },
          );
        }
      });
    });
  }, [
    accountTypes,
    generateMappingsAndTypes,
    hasMatchingTypes,
    hasMultipleFields,
    name,
    nodeFunction.token,
    setValue,
    sources,
    watch,
  ]);

  const handleAutoMapping = () => {
    setIsError(false);

    const timeout = setTimeout(() => {
      autoMapFields();
      setAutoMapperTimeout(undefined);
    }, 1000);

    setAutoMapperTimeout(timeout);
  };

  // Only display auto mapper if not disabled and has at least one account source.
  const shouldDisplayAutoMapper =
    !disabled &&
    sources.some(
      (source) =>
        source['object-type'] === 'Account' &&
        accountTypes.find((accountType) => source['object-type-token'] === accountType.id),
    );

  return (
    <>
      <Heading size='sm'>{PRESET_TYPE_TO_LABEL[presetSummary.preset]}</Heading>
      <Spacer height={2} />
      {shouldDisplayAutoMapper && (
        <SmartMappingBanner
          message='Suggest smart mappings for this node function.'
          loadingMessage='Suggesting smart mappings...'
          errorMessage='Unable to provide suggestions. Please try again.'
          onUseClick={handleAutoMapping}
          isLoading={!!autoMapperTimeout}
          isError={isError}
        />
      )}
      {sources.map((source, idx) => {
        const accountType = accountTypes.find(
          (accountType) => source['object-type-token'] === accountType.id,
        );
        return (
          <div key={`${source['object-type']}.${source['object-type-token']}.${source.path}`}>
            {source['object-type'] === 'Account' && accountType ? (
              <SourceFunctionMapping
                watch={watch}
                setValue={setValue}
                source={source}
                nodeFunctionToken={nodeFunction.token}
                accountType={accountType}
                presetSummary={presetSummary}
                presetSummaries={presetSummaries}
                disabled={disabled}
                sourceIdx={idx}
              />
            ) : (
              <SourceFunctionMappingFixed
                source={source}
                nodeFunctionToken={nodeFunction.token}
                presetSummary={presetSummary}
                sourceIdx={idx}
              />
            )}
            {idx !== sources.length - 1 && <Spacer height={3} />}
          </div>
        );
      })}
    </>
  );
};

export default NodeFunctionConfiguration;
